import { useCallback, useEffect, useMemo, useState } from 'react'
import { CircularProgress, useTheme } from '@mui/material'
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom'
import { Box, Button, Container, Stack, Typography } from '@mui/material'
import PortfolioHeatStress from '../components/climateAnalytics/portfolio/PortfolioHeatStress'
import PortfolioExtremeWind from '../components/climateAnalytics/portfolio/PortfolioExtremeWind'
import PortfolioFlood from '../components/climateAnalytics/portfolio/PortfolioFlood'
import PortfolioWaterRisk from '../components/climateAnalytics/portfolio/PortfolioWaterRisk'
import PortfolioWildfire from '../components/climateAnalytics/portfolio/PortfolioWildfire'
import siteMap from '../siteMap'
import { useClimateHazards } from '../hooks/analytics'
import { CanonicalPeriod, EmissionTier, parseCanonicalPeriod } from '../components/climateAnalytics/common'
import EmissionTierSelector from '../components/climateAnalytics/common/EmissionTierSelector'
import HorizonSelector from '../components/climateAnalytics/common/HorizonSelector'
import HazardSelector from '../components/climateAnalytics/common/HazardSelector'
import ClimateContextBar from '../components/climateAnalytics/common/ClimateContextBar'
import { getClimateHazardViewConfig } from '../components/climateAnalytics/common/hazardConfig'
import { ClimateAnalyticsPermissions, PermissionGate } from 'balkerne-core/roles'

function resolvePeriodForHazard(hazardCode: string | null, candidate: CanonicalPeriod | null | undefined): CanonicalPeriod {
  const config = getClimateHazardViewConfig(hazardCode)
  if (candidate && config.availablePeriods.includes(candidate)) return candidate
  return config.defaultPeriod
}

const ClimateAnalytics = () => {
  const history = useHistory()
  const location = useLocation()
  const hazardMatch = useRouteMatch<{ hazard?: string }>(`${siteMap.ClimateAnalytics.path}/:hazard`)
  const theme = useTheme()
  const { data: hazards, isLoading: isHazardsLoading } = useClimateHazards()
  const [emissionTier, setEmissionTier] = useState<EmissionTier>(() => {
    const param = new URLSearchParams(location.search).get('emission')
    if (param === EmissionTier.LOW || param === EmissionTier.MODERATE || param === EmissionTier.HIGH) {
      return param as EmissionTier
    }
    return EmissionTier.HIGH
  })
  const [selectedPeriod, setSelectedPeriod] = useState<CanonicalPeriod>(() => {
    const param = parseCanonicalPeriod(new URLSearchParams(location.search).get('period'))
    return param ?? CanonicalPeriod.LONG_TERM
  })
  const [selectedHazardCode, setSelectedHazardCode] = useState<string | null>(null)

  const hazardFromRoute = hazardMatch?.params?.hazard
  const emissionFromQuery = useMemo(() => {
    const param = new URLSearchParams(location.search).get('emission')
    if (param === EmissionTier.LOW || param === EmissionTier.MODERATE || param === EmissionTier.HIGH) {
      return param as EmissionTier
    }
    return null
  }, [location.search])
  const periodFromQuery = useMemo(() => parseCanonicalPeriod(new URLSearchParams(location.search).get('period')), [
    location.search,
  ])

  const hazard = useMemo(() => {
    return hazards.find(h => h.name === selectedHazardCode)
  }, [hazards, selectedHazardCode])

  const hazardViewConfig = useMemo(() => getClimateHazardViewConfig(selectedHazardCode), [selectedHazardCode])

  const currentHazardCode = useMemo(
    () => selectedHazardCode ?? hazards.find(h => h.name === hazardFromRoute)?.name ?? hazards[0]?.name ?? null,
    [hazardFromRoute, hazards, selectedHazardCode],
  )

  useEffect(() => {
    if (!hazards.length) return
    const fallbackHazard = hazards[0]?.name ?? null
    const nextHazard = hazards.find(h => h.name === hazardFromRoute)?.name ?? fallbackHazard
    if (!nextHazard) return
    const nextEmissionTier = emissionFromQuery ?? emissionTier
    const nextPeriod = resolvePeriodForHazard(nextHazard, periodFromQuery ?? selectedPeriod)

    if (selectedHazardCode !== nextHazard) {
      setSelectedHazardCode(nextHazard)
    }
    if (emissionFromQuery && emissionFromQuery !== emissionTier) {
      setEmissionTier(emissionFromQuery)
    }
    if (selectedPeriod !== nextPeriod) {
      setSelectedPeriod(nextPeriod)
    }

    const targetPath = siteMap.ClimateAnalytics.getPath(nextHazard, nextEmissionTier, nextPeriod)
    const currentPath = `${location.pathname}${location.search}`
    if (currentPath !== targetPath) {
      history.replace(targetPath)
    }
  }, [
    emissionFromQuery,
    emissionTier,
    hazards,
    hazardFromRoute,
    history,
    location.pathname,
    location.search,
    periodFromQuery,
    selectedHazardCode,
    selectedPeriod,
  ])

  const renderContent = useMemo(() => {
    switch (selectedHazardCode) {
      case 'fld':
        return <PortfolioFlood emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      case 'hs':
        return <PortfolioHeatStress emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      case 'tc':
        return <PortfolioExtremeWind emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      case 'wr':
        return <PortfolioWaterRisk emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      case 'wf':
        return <PortfolioWildfire emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      default:
        return null
    }
  }, [emissionTier, selectedHazardCode, selectedPeriod])

  const handleHazardChange = useCallback(
    (nextHazard: string) => {
      const nextPeriod = resolvePeriodForHazard(nextHazard, selectedPeriod)
      setSelectedHazardCode(nextHazard)
      setSelectedPeriod(nextPeriod)
      history.push(siteMap.ClimateAnalytics.getPath(nextHazard, emissionTier, nextPeriod))
    },
    [emissionTier, history, selectedPeriod],
  )

  const handleEmissionTierChange = useCallback(
    (nextEmissionTier: EmissionTier) => {
      if (!currentHazardCode) return
      setEmissionTier(nextEmissionTier)
      history.push(siteMap.ClimateAnalytics.getPath(currentHazardCode, nextEmissionTier, selectedPeriod))
    },
    [currentHazardCode, history, selectedPeriod],
  )

  const handlePeriodChange = useCallback(
    (nextPeriod: CanonicalPeriod) => {
      if (!currentHazardCode) return
      const resolvedPeriod = resolvePeriodForHazard(currentHazardCode, nextPeriod)
      setSelectedPeriod(resolvedPeriod)
      history.push(siteMap.ClimateAnalytics.getPath(currentHazardCode, emissionTier, resolvedPeriod))
    },
    [currentHazardCode, emissionTier, history],
  )

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar + 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'saturate(180%) blur(6px)',
        }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
          {/* Eyebrow + Primary actions (row 1) */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            py={1.5}>
            <Typography variant="overline" color="text.secondary">
              Climate Analytics
            </Typography>

            <Stack direction="row" spacing={1}>
              <PermissionGate action={ClimateAnalyticsPermissions.Modify}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => history.push(siteMap.ClimateAnalyticsConfigure.path)}>
                  Configure Vulnerability
                </Button>
              </PermissionGate>
              <Button
                size="small"
                variant="contained"
                onClick={() => history.push(siteMap.ClimateAnalyticsExport.path)}>
                Export Data
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={1.5} pb={2}>
            <Box sx={{ minWidth: 0, maxWidth: 760 }}>
              <Typography variant="h4" fontWeight={800}>
                {hazard ? hazard.display_name : 'Hazard'}
              </Typography>
              {hazard?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {hazard.description}
                </Typography>
              )}
            </Box>

            <ClimateContextBar
              hazardControl={
                <HazardSelector hazards={hazards} value={selectedHazardCode ?? ''} onChange={handleHazardChange} />
              }
              emissionControl={<EmissionTierSelector value={emissionTier} onChange={handleEmissionTierChange} />}
              horizonControl={
                <HorizonSelector
                  value={selectedPeriod}
                  availablePeriods={hazardViewConfig.availablePeriods}
                  onChange={handlePeriodChange}
                />
              }
            />
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      {isHazardsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
          {renderContent}
        </Container>
      )}
    </Box>
  )
}

export default ClimateAnalytics
